import { AccountingDashboardGuestOriginRow } from '@/renderer/types/accounting';
import { formatCompactCurrency } from './accountingDashboard';

interface AccountingMarketSnapshotProps {
  rows: AccountingDashboardGuestOriginRow[];
}

export function AccountingMarketSnapshot({
  rows,
}: AccountingMarketSnapshotProps) {
  const topRows = rows.slice(0, 5);
  const totalRevenue = topRows.reduce((sum, r) => sum + r.total_revenue, 0);

  return (
    <div className="rounded-[22px] border border-gray-200 bg-white p-3.5 shadow-sm">
      <div>
        <p className="text-sm font-semibold text-slate-900">Market snapshot</p>
        <p className="mt-1 text-xs text-slate-500">
          Top accommodation origins by revenue, with booking-value context.
        </p>
      </div>

      <div className="mt-3">
        {!topRows.length ? (
          <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-sm text-slate-500">
            No guest-origin activity is available in the selected period.
          </div>
        ) : (
          <div className="space-y-2">
            {topRows.map((row, index) => {
              const averageBookingValue =
                row.bookings_count > 0
                  ? row.total_revenue / row.bookings_count
                  : 0;
              const sharePct =
                totalRevenue > 0 ? (row.total_revenue / totalRevenue) * 100 : 0;

              return (
                <div
                  key={
                    row.country_id ||
                    row.country_iso_code ||
                    row.country_name ||
                    'unknown'
                  }
                  className="rounded-xl border border-gray-200 px-3 py-2.5"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-pos-primary/10 text-[10px] font-bold text-pos-primary">
                        {index + 1}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-semibold text-slate-900">
                          {row.country_name ||
                            row.country_iso_code ||
                            'Unknown'}
                        </p>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {row.bookings_count.toLocaleString()} booking(s) · Avg{' '}
                          {formatCompactCurrency(averageBookingValue)}
                        </p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <p className="text-sm font-semibold text-slate-900">
                        {formatCompactCurrency(row.total_revenue)}
                      </p>
                      <p className="mt-0.5 text-[10px] text-slate-400">
                        {sharePct.toFixed(1)}%
                      </p>
                    </div>
                  </div>
                  <div className="ml-7 mt-2 h-1 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className="h-full rounded-full bg-pos-primary"
                      style={{ width: `${sharePct}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
